import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { getEllipses, onGenderPronouns } from 'src/utils/helpers';
import { updateUserInfoV1 } from 'src/api/user';
import { setFlashNotification } from 'src/redux/modules/flashNotification';
import { fetchTherapistClientMetaData } from 'src/redux/actions/questionnaireActions';
import Input from 'src/components/base/Input/Input';
import Tooltip from 'src/components/base/Tooltip/Tooltip';
import { Pencil } from '../Pencil';
import { UserPronouns } from './constant';
import { User } from './types';
import './UserProfileHeader.scss';
import { useIsMobile } from "src/latest/src/hooks";
import { UserRoles } from '../../constants/UserRoles';
import { Typography } from "src/latest/src/components";


type props = {
  userProfileDetails: User;
  onUpdateUserDetails: (updatedUserInfo: any) => void
};

export default function UserInfo({
  userProfileDetails,
  onUpdateUserDetails,
}: props) {
  const dispatch = useDispatch();
  const isFromMobile = useIsMobile();
  const { loggedInUser } = useSelector(
    ({ authentication }: any) => authentication,
  );
  const [isProfileEditable, setIsProfileEditable] = useState<boolean>(false);
  const isLoggedinUserAdmin =
    loggedInUser?.user_type?.includes(UserRoles.PRACTICE_ADMIN);
  const [editingField, setEditingField] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [userCredential, setUserCredentials] = useState(
    userProfileDetails.credentials,
  );
  const [titleErrorMsg, setTitleErrorMsg] = useState<string>('');

  useEffect(() => {
    setUserCredentials(
      userProfileDetails?.credentials ? userProfileDetails.credentials : '',
    );

    toggleProfileEditableState();
  }, [userProfileDetails]);

  const toggleProfileEditableState = () => {
    if (loggedInUser?.id === userProfileDetails?.id) {
      setIsProfileEditable(true);
    } else if (
      isLoggedinUserAdmin &&
      (userProfileDetails?.user_type?.includes(UserRoles.THERAPIST) ||
        userProfileDetails?.user_type?.includes(UserRoles.SCHEDULER))
    ) {
      setIsProfileEditable(true);
    } else {
      setIsProfileEditable(false);
    }
  };

  const updateUserDetails = async (data: any) => {
    try {
      setEditingField({});
      const updatedUserInfo = await updateUserInfoV1(
        userProfileDetails?.id,
        data,
      );

      onUpdateUserDetails(updatedUserInfo);

      await dispatch(fetchTherapistClientMetaData(userProfileDetails?.id));

      dispatch(
        setFlashNotification({
          message: 'Successfully updated.',
          dispatchClass: 'notif-settings',
        }),
      );
    } catch (error) {
      dispatch(
        setFlashNotification({
          message: "There's an error saving account settings",
          isError: true,
          dispatchClass: 'notif-settings',
        }),
      );

      console.error(error);
      
    }
  };

  const toggleEditing = (key: string) => {
    setEditingField({ [key]: !editingField[key] });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setTitleErrorMsg('');

    if (name === 'credentials' && /[^0-9a-zA-Z, ]/g.test(e.target.value)) {
      return;
    }

    setUserCredentials(value.trimStart());
  };

  const onSelectPronouns = async (item: { value: string }) => {
    const dataToUpdate = {
      gender_pronoun: item.value,
    };

    updateUserDetails(dataToUpdate);
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const trimmedValue = value?.trim();

    if(!trimmedValue){

      setTitleErrorMsg('Title is required');

      return;
    }

    if(trimmedValue !== userProfileDetails?.credentials){
      setTitleErrorMsg('');
      const dataToUpdate = {
        credentials: trimmedValue,
      };

      updateUserDetails(dataToUpdate);
    } else {
      setTitleErrorMsg('');
      toggleEditing('credentials');
    }

  };

  return (
    <>
      {userProfileDetails && (
        <div className="user-name-information">
          <React.Fragment>
            <div className="user-name">
              <Typography variant={isFromMobile ? "size-20" : "size-34"} weight="regular" color="color-2" 
               className='name'>
                {userProfileDetails?.full_name !== ''
                  ? getEllipses(userProfileDetails?.full_name)
                  : ''}{userProfileDetails.credentials ? `,` : ''}

                {(userProfileDetails?.full_name?.trim() === '' ||
                  userProfileDetails?.full_name === undefined) &&
                  userProfileDetails?.email
                  ? getEllipses(userProfileDetails?.email)
                  : ''}
              </Typography>
                <div
                  className={`accountInformation__content-edit ${editingField && editingField.credentials
                    ? '-editing-name-therapist'
                    : ''
                    }`}
                >
                  {editingField && editingField.credentials ? (
                    <Input
                      value={userCredential}
                      onChange={onChange}
                      name="credentials"
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                        e.target.value === userProfileDetails.credentials
                          ? toggleEditing('credentials')
                          : onSubmit(e)
                      }
                      feedbackMessage={titleErrorMsg?.length ? titleErrorMsg : ''}
                      isInvalid={!!titleErrorMsg?.length}
                    />
                  ) : (
                    <Tooltip
                      text={userProfileDetails.credentials}
                      placement="right"
                      force={false}
                    >
                      <Typography
                        color="color-4"
                        variant="size-16"
                        weight="regular"
                        className="show-ellipsis"
                      >
                        <Typography
                          color="color-2"
                          variant={isFromMobile ? "size-20" : "size-34"}
                          weight="regular"
                          className='pronoun-text'
                        >
                          {userProfileDetails.credentials ? `${getEllipses(userCredential)}` : ''}
                        </Typography>
                        {isProfileEditable && (
                          <Pencil
                            onClick={() => toggleEditing('credentials')}
                          />
                        )}
                      </Typography>
                    </Tooltip>
                  )}
                </div>
              {/* )} */}
            </div>
            {userProfileDetails?.gender_pronoun && (
              <div
                className={`accountInformation__content-edit ${editingField && editingField.gender_pronoun
                  ? '-editing-pronouns'
                  : ''
                  }`}
              >
                {editingField && editingField.gender_pronoun ? (
                  <Dropdown className="accountInformation__dropdown">
                    <Dropdown.Toggle variant="outline-secondary">
                      <Typography
                        variant="size-12"
                        color="color-28"
                        weight="regular"
                        className="d-inline-block"
                      >
                        {onGenderPronouns(
                          userProfileDetails?.gender_pronoun ?? '',
                        )}
                      </Typography>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {UserPronouns.map(item => (
                        <Dropdown.Item
                          className="cursor-pointer"
                          key={item.value}
                        >
                          <Typography
                            variant="size-14"
                            weight="regular"
                            onClick={() => {
                              onSelectPronouns(item);
                              toggleEditing('gender_pronoun');
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <Typography
                    color="color-4"
                    variant="size-16"
                    weight="regular"
                    className="pt-1"
                  >
                    {onGenderPronouns(userProfileDetails?.gender_pronoun ?? '')}
                    {isProfileEditable && (
                      <Pencil onClick={() => toggleEditing('gender_pronoun')} />
                    )}
                  </Typography>
                )}
              </div>
            )}
          </React.Fragment>
        </div>
      )}
    </>
  );
}
